import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";

import arrowIcon from "../../assets/previous_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import useLanguage from "../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import { toMoney } from "../../utils/toMoney";
import CustomButton from "../../components/CustomButton";
import axios from "axios";
import PayslipTemplate from "./PayslipTemplate";
import wagesIcon from "../../assets/wages_icon.png";
import printIcon from "../../assets/printer_icon.png";
import MonthAttendancePDFTemplate from "./MonthAttendanceTemplate";
import Modal from "../../components/Modal";
import HotdropdownV2 from "../../components/HotdropdownV2";
import { getAllKassas } from "../../services/kassa/kassaServices";
import HotDropdown from "../../components/HotDropdown";
import PortalDropdown from "../../components/PortalDropdown";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import closeIcon from "../../assets/close_icon.png";
import checkIcon from "../../assets/check_icon.png";
import saveIcon from "../../assets/save_icon.png";
import { COMPANY_DATA } from "../../constants";
import { getOrganizationById } from "../../services/org";

function formatSalary(amount) {
  if (amount === null || amount === undefined) return "";

  // Convert to string
  let [integerPart, decimalPart] = amount.toString().split(".");

  // Add commas to integer part
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // If there is a decimal part, remove trailing zeros
  if (decimalPart) {
    decimalPart = decimalPart.replace(/0+$/, ""); // remove trailing zeros
    if (decimalPart === "") return integerPart; // if empty, no decimals left
    return `${integerPart}.${decimalPart}`;
  }

  return integerPart;
}

export const generatePDFDownload = async ({
  templateComponent: Template,
  fileName = "document.pdf",
  templateProps = {},
  // data,
  onStart = () => {},
  onComplete = () => {},
  onError = (err) => console.error("PDF generation error:", err),
}) => {
  onStart();
  try {
    const blob = await pdf(<Template {...templateProps} />).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 5000);

    onComplete();
  } catch (err) {
    onError(err);
    onComplete();
  }
};

const MonthlyAttendanceBar = ({
  data,
  employee,
  pagination,
  onPageChange,
  totalSalaryInMonth,
}) => {
  const [collapsed, setCollapsed] = useState({});

  const { t } = useTranslation("general");
  const { currentDirection, language } = useLanguage();
  const [showSelectKassaModal, setShowSelectKassaModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [issueSalaryLoading, setIssueSalaryLoading] = useState(false);
  const [issueSalaryError, setIssueSalaryError] = useState("");
  const [organizationInfo, setOrganizationInfo] = useState(null);

  // Helper functions
  const toggleCollapse = (month) => {
    setCollapsed((prev) => ({
      ...prev,
      [month]: !prev[month],
    }));
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDateWithDay = (dateString) => {
    const date = new Date(dateString);
    const dayName = t(
      [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ][date.getDay()]
    );
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");

    return `${dayName} ${day}-${month}`;
  };

  // ENDPOINT FUNCTIONS
  const issueSalary = async (month, year, kassa) => {
    try {
      setIssueSalaryLoading(true);
      setIssueSalaryError("");
      const token = localStorage.getItem("token");
      const organizationId = localStorage.getItem("organizationId");
      const employeeId = data[0]?.userId;

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/api/attendance/createPayslip`,
        {
          employeeId,
          organizationId,
          month,
          year,
          paymentType: "MONTHLY",
          paymentMethod: "CASH", // Default (can be dynamic)
          kassaId: kassa,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // alert("Salary issued successfully!");
        // Optionally refresh data or show a success message
      }

      if (response.data.success) {
        const payslip = response.data.data;
        const employee = payslip.employee;
        const calc = payslip.calculationDetails;

        await generatePDFDownload({
          templateComponent: PayslipTemplate,
          fileName: `Payslip-${employee.username}-${month}-${year}.pdf`,
          templateProps: {
            language: language === "ar" ? "AR" : "EN",
            companyData: {
              name: organizationInfo.organizationName,
              address: organizationInfo.address,
              phone: organizationInfo.phoneNumber,
              email: organizationInfo.email,
            },
            employeeData: {
              fullName: employee.username,
              address: employee.address,
              phone: employee.phoneNumber,
              email: employee.email,
            },
            payslipData: {
              payDate: new Date(payslip.payslipDate).toLocaleDateString(),
              payType: payslip.paymentType,
              payrollNumber: payslip.receiptNumber,
              paymentMethod: payslip.paymentMethod,
              period: `${month}-${year}`,
            },
            salaryData: {
              basicSalary: calc.baseMonthlySalary,
              grossSalary: calc.grossSalary,
              hourlyRate: employee.hourlySalary ?? 0,
              overtime: calc.overTimePay ?? 0,
              bonus: calc.bonuses ?? 0,
              allowances: 0,
              presentDays: calc.presentDays,
              absentDays: calc.absentDays,
              vacationDays: calc.vacationDays,
              expenses: calc.expenses ?? 0,
              deductions: calc.deductions ?? 0,
              tax: 0,
              insurance: 0,
              netPay: calc.netPay,
            },
            deductionsBreakddown:
              payslip.calculationDetails.deductionsBreakdown,
            entitlementsBreakdown:
              payslip.calculationDetails.entitlementsBreakdown,
          },
        });
      }
      setShowSelectKassaModal(false);
    } catch (error) {
      console.error("Error issuing salary:", error);
      setIssueSalaryError("errorIssueSalary");
    } finally {
      setIssueSalaryLoading(false);
    }
  };

  // HANDLER FUNCTIONS
  const handleSalaryIssue = (kassa, date) => {
    // Logic to handle salary issue
    const [year, month] = date.split("-");
    issueSalary(parseInt(month), parseInt(year), kassa);
  };

  // SIDE EFFECTS
  useEffect(() => {
    const initialCollapsed = {};
    data.forEach((employee) => {
      employee.data.forEach((monthGroup) => {
        initialCollapsed[monthGroup.month] = true;
      });
    });
    setCollapsed(initialCollapsed);
  }, [data]);

  const handlePrintMonthAttendance = async (monthGroup) => {
    const [year, month] = monthGroup.month.split("-");
    const attendanceDate = `${year}-${month}-01`;
    await generatePDFDownload({
      templateComponent: MonthAttendancePDFTemplate,
      fileName: `attendance-${employee.username}-${month}-${year}.pdf`,
      templateProps: {
        language: language === "ar" ? "AR" : "EN",
        companyData: {
          name: organizationInfo.organizationName,
          address: organizationInfo.address,
          phone: organizationInfo.phoneNumber,
          email: organizationInfo.email,
        },
        employeeData: {
          fullName: employee.username,
          address: employee.address,
          phone: employee.phone,
          email: employee.email,
        },
        attendanceData: {
          printDate: new Date().toLocaleDateString(),
          totalSalary: formatSalary(totalSalaryInMonth),
          dueSalary: formatSalary(monthGroup.totalSalary),
          paidSalary: formatSalary(monthGroup.expectedPaidAmount),
          attendanceDate: attendanceDate,
        },
        days: monthGroup.days.map((day) => ({
          ...day,
          salary: `${toMoney(day.salary)} ${t("IQD")}`,
          checkIn: formatTime(day.checkIn),
          checkOut: formatTime(day.checkOut),
          status: t(day.status.toLowerCase()),
        })),
        presentDays: monthGroup.days.filter((day) => day.status === "PRESENT")
          .length,
        absentDays: monthGroup.days.filter((day) => day.status === "ABSENT")
          .length,
        vacationDays: monthGroup.days.filter((day) => day.status === "VACATION")
          .length,
        deductions: monthGroup.days.filter((day) => day.status === "OFFDAY")
          .length,
      },
    });
  };

  useEffect(() => {
    const fetchOrganizationInfo = async () => {
      try {
        const organizationId = localStorage.getItem("organizationId");
        const response = await getOrganizationById(organizationId);
        setOrganizationInfo(response);
      } catch (error) {
        console.error("Error fetching organization info:", error);
      }
    };
    fetchOrganizationInfo();
  }, []);

  return (
    <div
      className="c-fabric-table o-column o-column--gap-size_400 u-margin u-margin--bottom_700 "
      dir={currentDirection === "rtl" ? "rtl" : "ltr"}
    >
      {data.map((employee) => {
        return employee.data.map((monthGroup) => (
          <div
            className="o-column o-column--gap-size_100"
            key={monthGroup.month}
          >
            <div className="c-fabric-block">
              <div
                className="c-fabric-header"
                onClick={() => toggleCollapse(monthGroup.month)}
              >
                <div className="c-collapse-icon" style={{ cursor: "pointer" }}>
                  <img
                    src={arrowIcon}
                    alt="Collapse"
                    style={{
                      width: 16,
                      transform: collapsed[monthGroup.month]
                        ? "rotate(0deg)"
                        : "rotate(-90deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </div>
                <div className="o-column-row-switcher u-width_full-width o-row o-row--horizontal-align_space-between">
                  <div className="o-column o-column--gap-size_200">
                    <span className="c-fabric-code">
                      {t("month")}: {monthGroup.month}
                    </span>

                    <span className="t-text-mini">
                      {t("dueSalary")}: {toMoney(monthGroup.totalSalary)}{" "}
                      {t("IQD")}
                    </span>

                    <span className="t-text-mini">
                      {t("payedSalary")}:{" "}
                      {toMoney(monthGroup.expectedPaidAmount)} {t("IQD")}
                    </span>
                  </div>
                  <div className="o-row o-row--gap-size_400 u-margin--top_400 o-row--horizontal-align_end">
                    <CustomButton
                      icon={printIcon}
                      title={t("print")}
                      onClick={() => handlePrintMonthAttendance(monthGroup)}
                      variant="gray"
                      iconSize={18}
                    />
                    <CustomButton
                      icon={wagesIcon}
                      variant="blank"
                      title={t("issueTheSalary")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMonth(monthGroup.month);
                        setShowSelectKassaModal(true);
                      }}
                      iconSize={18}
                    />
                  </div>
                </div>
              </div>

              {!collapsed[monthGroup.month] && (
                <table className="c-fabric-inner-table">
                  <thead>
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("status")}</th>
                      <th> {t("checkIn")}</th>
                      <th>{t("checkOut")} </th>
                      <th>{t("salary")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthGroup.days.map((day, index) => (
                      <tr key={index}>
                        <td>{formatDateWithDay(day.date)}</td>
                        <td>{t(day.status.toLowerCase())}</td>
                        <td>{formatTime(day.checkIn)}</td>
                        <td>{formatTime(day.checkOut)}</td>
                        <td>
                          {day?.salary?.toFixed(2)} {t("IQD")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ));
      })}

      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() => onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
              mobileEffect
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() => onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
              mobileEffect
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}

      {showSelectKassaModal && (
        <SelectKassaModal
          setShowSelectKassaModal={setShowSelectKassaModal}
          onConfirm={(selectedKassa) => {
            handleSalaryIssue(selectedKassa, selectedMonth);
          }}
          error={issueSalaryError}
          loading={issueSalaryLoading}
          onClose={() => {
            if (!issueSalaryLoading) {
              setShowSelectKassaModal(false);
              setIssueSalaryError(null);
              setIssueSalaryLoading(false);
            }
          }}
        />
      )}
    </div>
  );
};

function SelectKassaModal({ onClose, onConfirm, error, loading }) {
  const { t } = useTranslation("general");
  const [kassas, setKassas] = useState([]);
  const [kassaLoading, setKassasLoading] = useState(false);
  const [selectedKassa, setSelectedKassa] = useState(null);

  const fetchKassas = async () => {
    setKassasLoading(true);
    try {
      const response = await getAllKassas({
        page: 1,
        limit: 100,
      });

      if (response.status === 200) {
        setKassas(response.data.kassas);

        // Set first kassa as default if available
        if (response.data.kassas.length > 0) {
          const firstKassa = response.data.kassas[0];
          setSelectedKassa({
            title: firstKassa.name,
            value: firstKassa.id,
            kassaData: firstKassa,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching Kassas:", error);
      // setError("failedToFetchKassas");
    } finally {
      setKassasLoading(false);
    }
  };

  const transformKassasForDropdown = (kassasData) => {
    return kassasData.map((kassa) => ({
      title: kassa.name,
      value: kassa.id,
      kassaData: kassa,
    }));
  };

  useEffect(() => {
    fetchKassas();
  }, []);

  return (
    <Modal
      title={t("selectKassa")}
      onClose={() => onClose()}
      error={t(error)}
      footer={
        <>
          {loading && <Loader size={LOADER_SIZES.MEDIUM} />}
          <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              variant="blank"
              onClick={() => onClose()}
              icon={closeIcon}
              iconSize={14}
              disabled={loading}
            />
            <CustomButton
              title={t("confirm")}
              onClick={() => onConfirm(selectedKassa.value)}
              disabled={loading}
              icon={saveIcon}
              iconSize={14}
            />
          </div>
        </>
      }
    >
      {error && <ErrorBox>{t(error)}</ErrorBox>}
      <div className="o-column o-column--gap-size_200">
        <PortalDropdown
          options={transformKassasForDropdown(kassas)}
          selected={selectedKassa ?? {}}
          setSelected={(option) => setSelectedKassa(option)}
          optionsName="kassas"
          thick
          loading={kassaLoading}
        />
      </div>
    </Modal>
  );
}

export default MonthlyAttendanceBar;
